import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, ShieldCheck, Award, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WaveDivider } from "@/components/WaveDivider";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useI18n } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";
import { pic } from "@/lib/responsive-images";

const svcRoofing = pic("svc-roofing");
const svcSolar = pic("svc-solar");
const svcStorm = pic("svc-storm");
const svcGutters = pic("svc-gutters");
const svcInsurance = pic("svc-insurance");
const svcCommercial = pic("svc-commercial");
const svcHvac = pic("svc-hvac");
const servicesHero = pic("services-hero");
const ctaHome = pic("cta-home");

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar Services in New Mexico | AMR 3" },
      {
        name: "description",
        content:
          "Roof installation, replacement, repair, solar panel integration, storm damage restoration, gutters and insurance claim assistance across New Mexico.",
      },
      { property: "og:title", content: "Roofing & Solar Services | AMR 3 Design & Construction" },
      {
        property: "og:description",
        content:
          "Comprehensive roofing and solar services for residential and commercial properties across New Mexico.",
      },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-services.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-services.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Roofing & Solar Services — AMR 3" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-services.jpg" },
      { name: "twitter:title", content: "Roofing & Solar Services | AMR 3" },
      { name: "twitter:description", content: "Comprehensive roofing and solar services for residential and commercial properties across New Mexico." },
      { property: "og:url", content: "https://www.amr3designconstruction.com/services" },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/services" }],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  const { t } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();

  const primary = [
    { key: "1", service: "roofing" as const, image: svcRoofing, alt: "AMR 3 crew installing a premium asphalt shingle roof on a New Mexico home" },
    { key: "2", service: "solar" as const, image: svcSolar, alt: "Modern solar panel array being installed on a southwestern tile roof in Albuquerque" },
    { key: "3", service: "storm" as const, image: svcStorm, alt: "AMR 3 storm response crew installing emergency tarp on a damaged residential roof" },
  ];

  const additional = [
    { key: "1", slug: "gutters", image: svcGutters, alt: "Newly installed seamless aluminum gutters on a modern home" },
    { key: "2", slug: "insurance", image: svcInsurance, alt: "AMR 3 contractor reviewing insurance documents with a homeowner outside their roof" },
    { key: "3", slug: "commercial", image: svcCommercial, alt: "Commercial flat TPO roof installation on a large New Mexico facility" },
    { key: "4", slug: "hvac", image: svcHvac, alt: "HVAC technician installing a high-efficiency air conditioning unit beside a New Mexico home" },
  ];

  return (
    <>
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={servicesHero.src}
          srcSet={servicesHero.srcSet}
          sizes="100vw"
          alt="AMR 3 roofing crew and solar installation team working on a modern New Mexico home at golden hour"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.80)] via-[oklch(0.18_0.03_50/0.58)] to-[oklch(0.18_0.03_50/0.34)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.services") }]} />
          <p className="mb-4 inline-block rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
            {t("svc.badge")}
          </p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-white drop-shadow sm:text-5xl lg:text-[3.25rem]">
            {t("svc.h1.l1")}
            <br className="hidden sm:block" /> {t("svc.h1.l2")}
          </h1>
          
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-5 w-5" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-background py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">{t("svc.primary.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("svc.primary.sub")}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Last updated: <time dateTime="2026-10-15">October 15, 2026</time>
            </p>
          </div>

          <div className="mt-14 space-y-20 lg:space-y-28">
            {primary.map((s, i) => {
              const reverse = i % 2 === 1;
              return (
                <div
                  key={s.key}
                  id={s.service}
                  className={`scroll-mt-28 grid items-center gap-10 lg:grid-cols-2 lg:gap-14 ${i > 0 ? "border-t border-border pt-20 lg:pt-28" : ""} ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
                >
                  <div className="overflow-hidden rounded-sm shadow-lg">
                    <img
                      src={s.image.src}
                      srcSet={s.image.srcSet}
                      sizes="(max-width: 1024px) 100vw, 640px"
                      alt={s.alt}
                      width={1280}
                      height={896}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                    />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-primary sm:text-3xl">{t(`svc.p.${s.key}.title`)}</h3>
                    <p className="mt-4 text-muted-foreground">{t(`svc.p.${s.key}.body`)}</p>

                    <div className="mt-6 rounded-sm border border-border bg-secondary/60 p-5">
                      <p className="text-sm font-semibold text-primary">{t("svc.expect")}</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`svc.p.${s.key}.expect`)}</p>
                    </div>

                    <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
                      <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
                        <a href="tel:+15058680847" className="gap-2">
                          <Phone className="h-5 w-5" /> {t("common.callPhone")}
                        </a>
                      </Button>
                      <Button asChild variant="goldOutline" size="xl" className="w-full sm:w-auto">
                        <Link to="/contact" search={{ service: s.service }} hash="project-form">
                          {t("common.requestConsultation")}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />

      <section className="bg-secondary py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">{t("svc.add.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("svc.add.sub")}</p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {additional.map((a) => (
              <div key={a.key} id={a.slug} className="scroll-mt-28 overflow-hidden rounded-sm bg-card shadow-md transition-shadow hover:shadow-xl">
                <img src={a.image.src} srcSet={a.image.srcSet} sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw" alt={a.alt} width={800} height={622} loading="lazy" className="h-56 w-full object-cover" />
                <div className="p-6">
                  <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent/15 text-accent">
                    <BadgeCheck className="h-4 w-4" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-primary">{t(`svc.add.${a.key}.title`)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`svc.add.${a.key}.body`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <span className="inline-block rounded-full border border-border bg-background px-5 py-2 text-xs font-medium text-muted-foreground">
              {t("svc.coord")}
            </span>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--secondary)" bottomColor="var(--background)" />

      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <div className="inline-flex items-center gap-2 text-accent">
            <ShieldCheck className="h-5 w-5" />
            <h2 className="font-serif text-2xl font-bold text-primary sm:text-3xl">{t("svc.commit.title")}</h2>
          </div>
          <p className="mt-5 text-muted-foreground">
            {t("svc.commit.body1")}{" "}
            <strong className="text-primary">{t("svc.commit.body1b")}</strong>
            {t("svc.commit.body1c")}
          </p>
          <blockquote className="mx-auto mt-6 max-w-xl border-l-4 border-accent pl-5 text-left italic text-muted-foreground">
            {t("svc.commit.quote")}
          </blockquote>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-2 text-xs font-medium text-primary">
              <Award className="h-3.5 w-3.5 text-accent" /> {t("svc.chip.years")}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-2 text-xs font-medium text-primary">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" /> {t("svc.chip.licensed")}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-2 text-xs font-medium text-primary">
              <BadgeCheck className="h-3.5 w-3.5 text-accent" /> {t("svc.chip.certified")}
            </span>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden text-primary-foreground">
        <img
          src={ctaHome.src}
          srcSet={ctaHome.srcSet}
          sizes="100vw"
          alt="AMR 3 roofing and solar crew completing a premium installation on a New Mexico home at golden hour"
          width={1920}
          height={1280}
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.72)] via-[oklch(0.18_0.03_50/0.54)] to-[oklch(0.18_0.03_50/0.26)]" />
        <div className="absolute left-0 right-0 -top-px z-10">
          <WaveDivider flip color="var(--background)" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-24 pt-28 text-center sm:px-6 sm:py-28 sm:pt-32">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">{t("svc.cta.title")}</h2>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-5 w-5" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("hero.cta1")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
